import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import {
  LayoutDashboard,
  Book,
  Users,
  FolderOpen,
  Settings,
  UserCheck,
  GitCommit,
  Calendar,
  X,
  User,
  LogOut,
  ChevronDown,
  Globe,
  Box,
  Brain
} from 'lucide-react';

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    { name: 'Schedule', href: '/schedule', icon: Calendar },
    { name: 'Classes', href: '/classes', icon: Book },
    { name: 'Students', href: '/students', icon: Users },
    { name: 'Projects', href: '/projects', icon: FolderOpen },
    { name: 'Blocks', href: '/learning-blocks', icon: Box },
    { name: 'Enrollments', href: '/enrollments', icon: UserCheck },
    { name: 'Intelligence', href: '/intelligence', icon: Brain },
    { name: 'Portal', href: '/portal', icon: Globe },
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <div className={`fixed lg:static inset-y-0 left-0 z-50 w-64 lg:w-48 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 h-full transform transition-transform duration-300 ease-in-out flex flex-col ${
        isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        {/* Logo/Brand with close button on mobile */}
        <div className="px-4 lg:px-3 py-6 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center justify-between lg:justify-center relative">
            <img src="/logo_1.png" alt="Classtraks Logo" className="w-[130px] lg:w-[110px] h-auto lg:mx-auto" />
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors absolute right-0"
            >
              <X className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            </button>
          </div>
        </div>
      
      {/* Navigation */}
      <nav className="px-3 lg:px-2 py-6 space-y-2 overflow-y-auto flex-1">
        {navigation.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
          const showDivider = item.name === 'Settings';

          return (
            <div key={item.name}>
              {showDivider && (
                <div className="my-4 border-t border-gray-200 dark:border-gray-700"></div>
              )}
              <Link
                to={item.href}
                onClick={onClose}
                className={`flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                  isActive
                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Icon className={`${item.name === 'Blocks' ? 'mr-2 h-[21.6px] w-[21.6px]' : 'mr-3 h-5 w-5'}`} />
                {item.name}
              </Link>
            </div>
          );
        })}
      </nav>

      {/* User Profile Section */}
      <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <div className="relative flex justify-center">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 bg-blue-500 dark:bg-blue-600 rounded-full flex items-center justify-center mb-2">
                <User className="w-4 h-4 text-white" />
              </div>
              <div className="text-center">
                <div className="text-sm font-medium text-gray-900 dark:text-white">{user?.firstName} {user?.lastName}</div>
                <div className="text-xs text-gray-500 dark:text-gray-300">{user?.email}</div>
              </div>
            </div>
            <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
          </button>

          {/* User Menu Dropdown */}
          {showUserMenu && (
            <div className="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg">
              <div className="py-2">
                <button
                  onClick={() => {
                    navigate('/settings/profile');
                    setShowUserMenu(false);
                    onClose();
                  }}
                  className="w-full flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <User className="w-4 h-4 mr-3" />
                  Profile
                </button>
                <button
                  onClick={() => {
                    logout();
                    setShowUserMenu(false);
                    onClose();
                  }}
                  className="w-full flex items-center px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <LogOut className="w-4 h-4 mr-3" />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </>
  );
};

export default Sidebar;